/**
 * features/admissions/AdmissionWindowPanel.tsx
 * ------------------------------------------------------------------
 * Admin UI for `reportSchoolSettings/admissionWindow` — the doc that
 * controls the public application form. Two main controls:
 *
 *   - acceptingApplications (toggle): if false, the public form
 *     shows a "closed" message instead of the form.
 *   - allowedForms (multi-select): which forms are open. Empty array
 *     OR omitted means all forms are open (the default).
 *   - message (optional): the text shown on the closed-state screen.
 *
 * The doc is created on first save; before that, the public form
 * defaults to "open, all forms allowed".
 *
 * Visible only to admin / headteacher (matches the firestore.rules
 * write permission for reportSchoolSettings).
 * ------------------------------------------------------------------
 */
import { useState, useEffect } from "react";
import { doc, getDoc } from 'firebase/firestore';
// Batch A: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  Settings,
  Save,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Loader2,
  Globe,
  Link as LinkIcon,
  Copy,
} from "lucide-react";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { SCHOOL } from "@/lib/school.config";
import { Spinner } from "@/components/ui/primitives";
import { GradientHero, SoftCard, AccentButton } from "@/features/finance/ui";
import AdmissionFormTextEditor from "@/features/admissions/AdmissionFormTextEditor";
import type { AdmissionWindowSettings } from "@/types";

const FORM_LEVELS = [
  { id: "1", label: "Form 1" },
  { id: "2", label: "Form 2" },
  { id: "3", label: "Form 3" },
  { id: "4", label: "Form 4" },
  { id: "5", label: "Form 5" },
  { id: "AS", label: "AS Level" },
  { id: "A2", label: "A2 Level" },
];

const SETTINGS_DOC_PATH = ["reportSchoolSettings", "admissionWindow"] as const;

export default function AdmissionWindowPanel() {
  const { userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const [accepting, setAccepting] = useState(true);
  const [allowedForms, setAllowedForms] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [originalState, setOriginalState] = useState<string>(""); // for dirty-detect

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, ...SETTINGS_DOC_PATH));
        if (snap.exists()) {
          const d = snap.data() as AdmissionWindowSettings;
          setAccepting(d.acceptingApplications !== false);
          setAllowedForms(new Set(d.allowedForms ?? []));
          setMessage(d.message ?? "");
          setOriginalState(
            JSON.stringify({
              accepting: d.acceptingApplications !== false,
              forms: (d.allowedForms ?? []).slice().sort(),
              message: d.message ?? "",
            }),
          );
        } else {
          // Default state (matches what the public form assumes)
          setAccepting(true);
          setAllowedForms(new Set());
          setMessage("");
          setOriginalState(
            JSON.stringify({ accepting: true, forms: [], message: "" }),
          );
        }
      } catch (e) {
        setMsg({ ok: false, text: (e as Error).message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentState = JSON.stringify({
    accepting,
    forms: Array.from(allowedForms).sort(),
    message,
  });
  const dirty = currentState !== originalState;

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    setSaveModal({ show: true, status: 'saving', message: 'Saving settings…' });
    try {
      const trimmed = message.trim();
      const payload: AdmissionWindowSettings = {
        id: "admissionWindow",
        acceptingApplications: accepting,
        allowedForms: Array.from(allowedForms).sort(),
        // Only include `message` when set — Firestore rejects `undefined`,
        // and we don't want an empty-string field cluttering the doc.
        ...(trimmed ? { message: trimmed } : {}),
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid ?? "",
      };
      await setDoc(doc(db, ...SETTINGS_DOC_PATH), payload, { merge: true });
      setOriginalState(currentState);
      setSaveModal({ show: true, status: 'success', message: 'Settings saved.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function toggleForm(id: string) {
    setAllowedForms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const applyUrl = `${SCHOOL.appUrl}/?form=apply`;
  const allFormsOpen = allowedForms.size === 0;

  if (loading) return <Spinner label="Loading settings…" />;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Settings}
        title="Application Form Settings"
        subtitle="Control what the public application form accepts"
      />

      {msg && (
        <div
          className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${
            msg.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              : "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:text-red-300"
          }`}
        >
          {msg.ok ? (
            <CheckCircle2 size={16} className="mt-0.5" />
          ) : (
            <AlertCircle size={16} className="mt-0.5" />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      {/* ---- Form URL block ---- */}
      <SoftCard>
        <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
          <LinkIcon size={16} className="text-primaryLight" />
          Public form link
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          Share this URL on the school website. Applicants do not need to sign
          in.
        </p>
        <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <Globe size={14} className="text-gray-400 shrink-0" />
          <code className="text-sm flex-1 truncate text-gray-700 dark:text-gray-200">
            {applyUrl}
          </code>
          <button
            onClick={() => navigator.clipboard?.writeText(applyUrl)}
            className="text-xs text-primaryLight hover:underline flex items-center gap-1 shrink-0"
            title="Copy URL"
          >
            <Copy size={12} /> Copy
          </button>
          <a
            href={applyUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primaryLight hover:underline flex items-center gap-1 shrink-0"
            title="Open form"
          >
            <ExternalLink size={12} /> Open
          </a>
        </div>
      </SoftCard>

      {/* ---- Accepting toggle ---- */}
      <SoftCard>
        <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
          <Settings size={16} className="text-primaryLight" />
          Accepting applications
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          When turned off, the public form shows a "currently closed" message
          instead of the form. Submissions are blocked at the server.
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <button
            type="button"
            onClick={() => setAccepting((v) => !v)}
            className={[
              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
              accepting ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600",
            ].join(" ")}
            aria-pressed={accepting}
          >
            <span
              className={[
                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                accepting ? "translate-x-6" : "translate-x-1",
              ].join(" ")}
            />
          </button>
          <span className="text-sm text-gray-900 dark:text-white">
            <strong>{accepting ? "OPEN" : "CLOSED"}</strong>
            {accepting
              ? " — the public form is currently accepting applications."
              : " — the public form will show a closed message."}
          </span>
        </label>
      </SoftCard>

      {/* ---- Allowed forms ---- */}
      <SoftCard>
        <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
          <Settings size={16} className="text-primaryLight" />
          Forms accepting applications
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Select which forms the public form should offer to applicants.
          {allFormsOpen
            ? " Currently set to all forms."
            : ` Currently restricted to ${allowedForms.size} form(s).`}
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {FORM_LEVELS.map((f) => {
            const selected = allowedForms.has(f.id);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => toggleForm(f.id)}
                disabled={!accepting}
                className={[
                  "px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors",
                  selected
                    ? "border-primaryLight bg-primaryLight text-white"
                    : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300",
                  !accepting ? "opacity-50 cursor-not-allowed" : "",
                ].join(" ")}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-400">
          Leave all unselected to accept applications for every form.
          {allFormsOpen && (
            <strong className="text-gray-500">
              {" "}
              — currently set to all forms.
            </strong>
          )}
        </p>
      </SoftCard>

      {/* ---- Closed-state message ---- */}
      <SoftCard>
        <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
          <Settings size={16} className="text-primaryLight" />
          Closed-state message
          <span className="text-xs font-normal text-gray-400">(optional)</span>
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          Shown to visitors when the form is set to CLOSED. Leave blank to use
          the default message.
        </p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight text-sm"
          placeholder={`${SCHOOL.name} is not currently accepting applications. Please check back later or contact the admissions office.`}
        />
        <p className="text-xs text-gray-400 mt-1 text-right">
          {message.length}/500
        </p>
      </SoftCard>

      {/* ---- Save bar ---- */}
      <div className="flex items-center justify-end gap-3">
        {dirty && (
          <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertCircle size={12} />
            Unsaved changes
          </span>
        )}
        <AccentButton
          icon={saving ? Loader2 : Save}
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? "Saving…" : "Save settings"}
        </AccentButton>
      </div>
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />

      {/* ---- Printed-form boilerplate editor (fees / docs / conditions) ---- */}
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700" />
      <AdmissionFormTextEditor />
    </div>
  );
}
